import { AfterViewInit, Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { SiteMapService } from '../site-map.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AppTextService } from '@fi-shared/app-text';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { EditTabModel, EditTabsModel } from '../../utilities/edit-tabs/edit-tabs.model';
import { NgForm } from '@angular/forms';
import { ObjectTitleService } from '../../utilities/fi-services/object-title.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { SiteMapEditGeneralComponent } from '@fi-app/site-map/site-map-edit/general/site-map-edit-general.component';
import { first } from 'rxjs/operators';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { SiteMapHelperService } from '@fi-shared/fi-shared-services/site-map-helper.service';
import { ISiteMap, ISiteMapTag } from '@fi-shared/models/site-map';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'fi-site-map-edit',
    templateUrl: './site-map-edit.component.html',
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-site-map-edit' },
    standalone: false,
})
export class SiteMapEditComponent implements OnInit, AfterViewInit {
    @Input() siteMap: ISiteMap;
    @Input() allTags;

    @ViewChild('siteMapEditForm', { static: false }) siteMapEditForm: NgForm;
    @ViewChild(SiteMapEditGeneralComponent, { static: false }) siteMapEditGeneralComponent: SiteMapEditGeneralComponent;

    editTabs: EditTabsModel;
    siteMapContentListDataLoaded$ = new BehaviorSubject<boolean>(false);

    constructor(
        private loadingService: LoadingService,
        private siteMapService: SiteMapService,
        private fiStateService: FiStateService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private saveChangesWarningService: SaveChangesWarningService,
        private objectTitleService: ObjectTitleService,
        private siteMapHelperService: SiteMapHelperService,
    ) {}

    ngOnInit() {
        this.setPageHeaderData();

        if (this.siteMap.Id) {
            this.siteMapContentListDataLoaded$.next(true);
        }
    }

    ngAfterViewInit(): void {
        const getModelData = () => {
            return {
                siteMap: this.siteMap,
            };
        };

        this.siteMapEditGeneralComponent.accessRightFilters.onInitialized$.pipe(first()).subscribe(() => {
            this.saveChangesWarningService.start(getModelData);
        });
    }

    getHeaderTitle(): string {
        return this.objectTitleService.GetTitle(this.siteMap.Id, this.siteMap.Name, ObjectTypes.SiteMap);
    }

    saveSiteMap($event?) {
        this.siteMapEditForm.onSubmit($event);

        if (this.siteMapEditForm.valid) {
            // we should skip properties because of converting circular structure to JSON error, try use in typescript
            // https://stackoverflow.com/questions/41102193/how-to-ignore-an-entity-field-in-typescript-when-sending-a-json-to-a-web-service
            const siteMapStr: string = JSON.stringify(this.siteMap, (key: string, value: string) => {
                if (key.startsWith('$$')) {
                    return undefined;
                }

                return value;
            });
            const siteMapToSave = JSON.parse(siteMapStr);
            this.loadingService.trackPromise(this.siteMapService.saveSiteMap(siteMapToSave)).then((data: number) => {
                this.siteMap.Id = data;
                this.saveChangesWarningService.stop();
                this.siteMapEditGeneralComponent.accessRightFilters.OnAccessRightFilterCollectionSaved();
                this.fiStateService.go(FiStateNames.SiteMap.List);
            });
        } else {
            this.fiDialogsService.error({
                messageText: this.appTextService.get('common.error_all_mandatory'),
            });
        }
    }

    onTagIdChange() {
        this.siteMapContentListDataLoaded$.next(false);
        this.siteMap.TagData = null;
        if (!this.siteMap.TagId) return;

        this.siteMapService.getSiteMapItemsByTagId(this.siteMap.TagId).then((response: ISiteMapTag) => {
            this.siteMap.TagData = response;
            this.siteMapContentListDataLoaded$.next(true);
        });
    }

    sortSiteMapBySortType() {
        this.siteMapContentListDataLoaded$.next(false);
        this.siteMap.TagData.ChildItems = this.siteMapHelperService.getSortedTagData(
            this.siteMap.TagData.ChildItems,
            this.siteMap.SortingType,
        );
        setTimeout(() => {
            this.siteMapContentListDataLoaded$.next(true);
        });
    }

    private setPageHeaderData() {
        this.editTabs = new EditTabsModel(this.getHeaderTitle());
        this.editTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('common.general'), this.appTextService.get('common.general')),
        );
        this.editTabs.Tabs.push(
            new EditTabModel(
                this.appTextService.get('sitemap.sitemap_content'),
                this.appTextService.get('sitemap.sitemap_content'),
            ),
        );
    }
}
